/**
 * /faqs — Frequently Asked Questions.
 *
 * Static content page — no backend needed. Uses <details> for a native,
 * keyboard-accessible accordion so seniors and screen-reader users can
 * navigate every question without JavaScript.
 */
import { Link } from "react-router-dom";
import { HelpCircle, ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const FAQ_GROUPS = [
  {
    title: "The basics",
    items: [
      {
        q: "What is Social Capital?",
        a: "Social Capital is the lifetime record of every meaningful contribution you make — helping neighbours, planting trees, mentoring students, rescuing food, documenting family history, and dozens more. Unlike likes or follows, it doesn't disappear into yesterday's feed. It grows into a portfolio that reflects who you are, not how loud you post.",
      },
      {
        q: "Who can build Social Capital?",
        a: "Anyone. Individuals, students, teachers, families, volunteers, businesses, nonprofits, schools and governments can all earn Social Capital. There's no age limit and no cost.",
      },
      {
        q: "Does it cost anything?",
        a: "No. Everything you do on SocialCapital.cc is free. We're funded by the sister-site network (FoodPulse, IndianFamilyTree, SocialPulse Business Portal, etc.) — never by selling your data.",
      },
    ],
  },
  {
    title: "How points work",
    items: [
      {
        q: "How do I earn points?",
        a: "By doing something that helps a person, a community, the environment, or a piece of knowledge — and then logging it here. The formula is: Base Value × Verification × Impact × Quality × Trust × Rarity = Social Capital Points. Small acts add up; big verified actions weigh more.",
      },
      {
        q: "Do points ever expire?",
        a: "No. Your lifetime record never resets. We do give a small weight to recent activity so a currently-active member is visible in leaderboards, but the underlying record is permanent.",
      },
      {
        q: "Who verifies my contributions?",
        a: "It depends on the activity. Some are self-declared, others are community-endorsed (an organiser or peer confirms), some are institution-verified (a nonprofit, school or municipality confirms), and a handful are system-verified (like nature observations we can automatically cross-check).",
      },
    ],
  },
  {
    title: "Privacy & control",
    items: [
      {
        q: "Can I keep my passport private?",
        a: "Yes. Every entry has a visibility flag — private, friends, community, or public. You can also make your entire passport private and only share a temporary link with an employer, admissions committee, or family member.",
      },
      {
        q: "Can I delete a contribution?",
        a: "Yes. You control your own record. Deleted entries stop counting toward your total immediately, though we keep a hash for fraud-prevention audits.",
      },
      {
        q: "What if a reviewer rejects my submission?",
        a: "You'll get a note explaining why and you can either edit and re-submit, or appeal it in the 'Corrections & Appeals' section of My Passport.",
      },
    ],
  },
  {
    title: "The bigger picture",
    items: [
      {
        q: "How does this connect to the other SocialPulse sites?",
        a: "Actions on FoodPulse (rescuing food), NaturePulse (observations, tree planting), FarmersPulse (supporting local farms), AlumniPulse (mentoring), IndianFamilyTree (documenting heritage) and the SocialPulse Business Portal all flow into a single Social Capital passport. One record, many surfaces.",
      },
      {
        q: "Can organizations have a Social Capital record too?",
        a: "Yes — we're building organizational Social Capital next. Nonprofits, schools and responsible businesses will be able to accumulate their own lifetime record based on verified actions of their members.",
      },
      {
        q: "What happens to my passport when I'm no longer here?",
        a: "Your contributions can live on as a legacy record on IndianFamilyTree or as a memorial page — a public reckoning of what you gave to the world. You (or a family member you designate) control what stays and what's private.",
      },
    ],
  },
];

export default function SocialCapitalFaqPage() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-10 md:py-14" data-testid="sc-faq-page">
      <SeoHead section="social-capital"
               title="Frequently Asked Questions · SocialCapital.cc"
               description="Answers to the most common questions about Social Capital points, verification, privacy and legacy."
               url="/faqs" />

      {/* Hero */}
      <header className="mb-10">
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest mb-3"
             style={{ color: "#059669" }}>
          <HelpCircle className="w-3.5 h-3.5" /> About · FAQs
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold" style={{ color: "#0f172a" }}>
          Frequently Asked Questions
        </h1>
        <p className="mt-4 opacity-80 text-base sm:text-lg max-w-2xl">
          Short answers to the questions we hear most often. Can't find yours?
          <Link to="/contact" className="ml-1 font-semibold" style={{ color: "#059669" }}>
            Send it to us
          </Link>.
        </p>
      </header>

      {/* Groups */}
      <div className="space-y-10">
        {FAQ_GROUPS.map((g, gi) => (
          <section key={g.title} data-testid={`sc-faq-group-${gi}`}>
            <h2 className="font-display text-xl font-bold mb-3" style={{ color: "#065F46" }}>
              {g.title}
            </h2>
            <div className="rounded-2xl border overflow-hidden bg-white" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
              {g.items.map((item, ii) => (
                <details key={ii} className="group border-b last:border-b-0"
                         style={{ borderColor: "rgba(0,0,0,0.06)" }}
                         data-testid={`sc-faq-item-${gi}-${ii}`}>
                  <summary
                    className="flex items-start justify-between gap-4 cursor-pointer px-5 py-4 list-none hover:bg-emerald-50/50"
                  >
                    <span className="font-semibold text-base sm:text-lg leading-snug">{item.q}</span>
                    <span
                      className="mt-1 shrink-0 w-6 h-6 rounded-full inline-flex items-center justify-center text-white text-sm"
                      style={{ background: "#059669" }}
                      aria-hidden
                    >+</span>
                  </summary>
                  <div className="px-5 pb-5 text-base opacity-85 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Footer CTA */}
      <aside
        className="mt-12 rounded-2xl border p-6 flex flex-wrap items-center justify-between gap-4"
        style={{ borderColor: "rgba(5,150,105,0.25)", background: "#ECFDF5" }}
      >
        <div>
          <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "#059669" }}>
            Still curious?
          </div>
          <div className="font-display text-lg mt-1">
            Read the full manifesto — <span style={{ color: "#059669" }}>Building Social Capital for Everyone</span>.
          </div>
        </div>
        <Link to="/social-capital"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white"
              style={{ background: "#059669" }}
              data-testid="sc-faq-manifesto-cta">
          Read the manifesto <ArrowRight className="w-4 h-4" />
        </Link>
      </aside>
    </div>
  );
}
